import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Minus, Trash2, ShoppingCart, Package, Search, Receipt, X, Building2, ImagePlus, CloudOff, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { addDoc, collection, doc, getDoc, getDocFromServer, getDocs, query, runTransaction, serverTimestamp, waitForPendingWrites, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { updateDocument, deleteDocument } from '../../services/firestoreService';
import { db, storage } from '../../services/firebase';
import LoadingState from '../shared/LoadingState';
import { filterEstablishmentsForUser } from '../../utils/establishmentAccess';
import { buildOrderTrackingPayload, normalizePhoneKey } from '../../utils/orderTracking';
import { buildCustomerComandaData, buildOrderGroupKey, buildOrderStatusNotification, buildPosPaymentRecord, calculatePaymentBreakdown } from '../../utils/pointOfSale';
import './PointOfSale.css';

const productCategories = [
  { value: 'food', label: 'Alimentação' },
  { value: 'drink', label: 'Bebidas' },
  { value: 'tour', label: 'Passeios' },
  { value: 'lodging', label: 'Hospedagem' },
  { value: 'boat', label: 'Embarcações' },
  { value: 'other', label: 'Outros' }
];

const paymentMethods = [
  { value: 'cash', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'debit', label: 'Débito' },
  { value: 'credit', label: 'Crédito' },
  { value: 'mixed', label: 'Misto' }
];

const getCategoryLabel = (category) => (
  productCategories.find(item => item.value === category)?.label || 'Outros'
);

const initialSaleData = {
  customerName: '',
  customerPhone: '',
  customerCpf: '',
  table: '',
  paymentMethod: 'cash',
  peopleCount: 1,
  notes: ''
};

const normalizeSaleItem = (item = {}) => {
  const price = Number(item.price) || 0;
  const quantity = Number(item.quantity) || 1;

  return {
    productId: item.productId || item.menuItemId || item.id || '',
    source: item.source || 'pos',
    name: item.name || 'Item sem nome',
    category: item.category || '',
    price,
    quantity,
    total: price * quantity
  };
};

const PointOfSale = () => {
  const { currentUser, userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState('');
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cashSession, setCashSession] = useState(null);
  const [cashAmount, setCashAmount] = useState('');
  const [selectedOrderGroup, setSelectedOrderGroup] = useState(null);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showProductsPanel, setShowProductsPanel] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState('');
  const [uploadingProductImage, setUploadingProductImage] = useState(false);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [pendingOperations, setPendingOperations] = useState([]);
  const [savingSale, setSavingSale] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState([
    { method: 'cash', amount: '' }
  ]);
  const [saleData, setSaleData] = useState(initialSaleData);
  const [productForm, setProductForm] = useState({
    name: '',
    category: 'food',
    price: '',
    stock: '',
    trackStock: false,
    imageUrl: '',
    active: true
  });

  const companyId = userData?.role === 'collaborator'
    ? userData?.currentCompanyId
    : userData?.companyId;
  const pendingStorageKey = companyId && currentUser?.uid
    ? `sdc-pos-pending:${companyId}:${currentUser.uid}`
    : '';

  const persistPendingOperations = useCallback((updater) => {
    setPendingOperations(current => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (pendingStorageKey) {
        localStorage.setItem(pendingStorageKey, JSON.stringify(next));
      }
      return next;
    });
  }, [pendingStorageKey]);

  const registerPendingOperation = useCallback((operation, commitPromise) => {
    persistPendingOperations(current => [
      ...current.filter(item => item.id !== operation.id),
      { ...operation, status: 'pending', createdAt: new Date().toISOString() }
    ]);

    commitPromise
      .then(() => {
        persistPendingOperations(current => current.filter(item => item.id !== operation.id));
      })
      .catch(error => {
        console.error('Erro ao sincronizar operação offline do PDV:', error);
        persistPendingOperations(current => current.map(item => (
          item.id === operation.id
            ? { ...item, status: 'error', errorMessage: error.message || 'Falha na sincronização' }
            : item
        )));
      });
  }, [persistPendingOperations]);

  const createOfflineOperation = (type, primaryPath, description) => ({
    id: `${currentUser?.uid || 'user'}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    primaryPath,
    description
  });

  const getTrackingRef = (orderId, phone) => {
    const phoneKey = normalizePhoneKey(phone);
    if (!phoneKey) return null;
    return doc(db, `companies/${companyId}/publicOrderStatus/${phoneKey}/orders`, orderId);
  };

  useEffect(() => {
    if (!pendingStorageKey) {
      setPendingOperations([]);
      return;
    }
    try {
      setPendingOperations(JSON.parse(localStorage.getItem(pendingStorageKey) || '[]'));
    } catch (error) {
      localStorage.removeItem(pendingStorageKey);
      setPendingOperations([]);
    }
  }, [pendingStorageKey]);

  useEffect(() => {
    const updateConnection = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateConnection);
    window.addEventListener('offline', updateConnection);
    return () => {
      window.removeEventListener('online', updateConnection);
      window.removeEventListener('offline', updateConnection);
    };
  }, []);

  useEffect(() => {
    if (!isOnline || pendingOperations.length === 0) return undefined;
    let cancelled = false;

    const confirmSyncedOperations = async () => {
      try {
        await waitForPendingWrites(db);
        const confirmations = await Promise.all(pendingOperations.map(async operation => {
          if (!operation.primaryPath) return operation.id;
          const snapshot = await getDocFromServer(doc(db, operation.primaryPath));
          return snapshot.exists() && snapshot.data().offlineOperationId === operation.id
            ? operation.id
            : null;
        }));
        if (!cancelled) {
          const confirmedIds = new Set(confirmations.filter(Boolean));
          if (confirmedIds.size > 0) {
            persistPendingOperations(current => current.filter(item => !confirmedIds.has(item.id)));
          }
        }
      } catch (error) {
        console.warn('Não foi possível confirmar a sincronização do PDV:', error);
      }
    };

    confirmSyncedOperations();
    return () => { cancelled = true; };
  }, [isOnline, pendingOperations, persistPendingOperations]);

  useEffect(() => {
    if (companyId) {
      loadEstablishments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId && selectedEstablishmentId) {
      loadPointOfSale();
    } else {
      setProducts([]);
      setSales([]);
      setOrders([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedEstablishmentId]);

  const selectedEstablishment = useMemo(() => (
    establishments.find(establishment => establishment.id === selectedEstablishmentId)
  ), [establishments, selectedEstablishmentId]);

  const loadEstablishments = async () => {
    setLoading(true);
    try {
      const [snapshot, activeCashSnapshot] = await Promise.all([
        getDocs(collection(db, `companies/${companyId}/establishments`)),
        currentUser?.uid
          ? getDoc(doc(db, `companies/${companyId}/activeCashRegisters`, currentUser.uid))
          : Promise.resolve(null)
      ]);
      const data = snapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(item => item.active !== false && ['bar', 'restaurant', 'kiosk'].includes(item.type))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      const allowed = filterEstablishmentsForUser(data, userData);
      setEstablishments(allowed);
      const activeEstablishmentId = activeCashSnapshot?.exists?.()
        ? activeCashSnapshot.data().establishmentId
        : '';
      if (activeEstablishmentId && allowed.some(item => item.id === activeEstablishmentId)) {
        setSelectedEstablishmentId(activeEstablishmentId);
      } else if (allowed.length === 1) {
        setSelectedEstablishmentId(allowed[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar pontos de venda do PDV:', error);
      alert('Erro ao carregar pontos de venda do PDV');
    } finally {
      setLoading(false);
    }
  };

  const loadPointOfSale = async () => {
    setLoadingRegister(true);
    try {
      const menuItemsRef = collection(db, `companies/${companyId}/establishments/${selectedEstablishmentId}/menuItems`);
      const [menuSnapshot, productsSnapshot, salesSnapshot, ordersSnapshot, sessionsSnapshot] = await Promise.all([
        getDocs(menuItemsRef),
        getDocs(query(collection(db, `companies/${companyId}/posProducts`), where('establishmentId', '==', selectedEstablishmentId))),
        getDocs(query(collection(db, `companies/${companyId}/posSales`), where('establishmentId', '==', selectedEstablishmentId))),
        getDocs(query(collection(db, `companies/${companyId}/orders`), where('establishmentId', '==', selectedEstablishmentId))),
        getDocs(query(collection(db, `companies/${companyId}/cashSessions`), where('establishmentId', '==', selectedEstablishmentId)))
      ]);

      const menuProducts = menuSnapshot.docs.map(item => ({
        id: item.id,
        productId: item.id,
        source: 'menu',
        active: item.data().active !== false,
        trackStock: false,
        ...item.data()
      }));

      const extraProducts = productsSnapshot.docs.map(item => ({
        id: item.id,
        productId: item.id,
        source: 'pos',
        ...item.data()
      }));

      const salesData = salesSnapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return bTime - aTime;
        });

      const ordersData = ordersSnapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(order => order.paymentStatus !== 'paid' && order.status !== 'cancelled')
        .sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return bTime - aTime;
        });

      const openSession = sessionsSnapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(session => session.status === 'open' && (
          session.openedByUserId === currentUser?.uid ||
          (!session.openedByUserId && session.openedByEmail === userData?.email)
        ))
        .sort((a, b) => {
          const aTime = a.openedAt?.toDate ? a.openedAt.toDate().getTime() : 0;
          const bTime = b.openedAt?.toDate ? b.openedAt.toDate().getTime() : 0;
          return bTime - aTime;
        })[0] || null;

      setProducts([...menuProducts, ...extraProducts]);
      setSales(salesData);
      setOrders(ordersData);
      setCashSession(openSession);
    } catch (error) {
      console.error('Erro ao carregar PDV:', error);
      alert('Erro ao carregar PDV');
    } finally {
      setLoadingRegister(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products
      .filter(product => product.active !== false)
      .filter(product => !term || product.name?.toLowerCase().includes(term) || getCategoryLabel(product.category).toLowerCase().includes(term));
  }, [products, search]);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    return { subtotal, itemCount };
  }, [cart]);

  const selectedGroupSummary = useMemo(() => {
    if (!selectedOrderGroup) return [];

    return selectedOrderGroup.orders.flatMap(order => {
      const items = Array.isArray(order.items) ? order.items : [];
      return items.map(item => ({
        id: `${order.id}-${item.productId || item.menuItemId || item.name}`,
        name: item.name,
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
        total: (Number(item.price) || 0) * (Number(item.quantity) || 1),
        orderId: order.id
      }));
    });
  }, [selectedOrderGroup]);

  const selectedGroupTotal = useMemo(() => {
    if (!selectedOrderGroup) return totals.subtotal;
    return Number(selectedOrderGroup.totalAmount) || 0;
  }, [selectedOrderGroup, totals.subtotal]);

  const paymentSummary = useMemo(() => {
    const total = selectedOrderGroup ? selectedGroupTotal : totals.subtotal;
    return calculatePaymentBreakdown({
      totalAmount: total,
      peopleCount: Number(saleData.peopleCount) || 1,
      paymentBreakdown: paymentBreakdown.map(row => ({
        method: row.method,
        amount: Number(row.amount) || 0
      }))
    });
  }, [selectedOrderGroup, selectedGroupTotal, totals.subtotal, saleData.peopleCount, paymentBreakdown]);

  const orderGroups = useMemo(() => {
    const groups = {};
    orders.forEach(order => {
      const key = buildOrderGroupKey(order);
      if (!groups[key]) {
        groups[key] = {
          key,
          label: order.customerPhone || order.table || order.customerName || 'Pedido avulso',
          type: order.customerPhone ? 'WhatsApp' : order.table ? 'Mesa' : 'Pedido',
          customerName: order.customerName || '',
          customerPhone: order.customerPhone || '',
          customerCpf: order.customerCpf || '',
          table: order.table || '',
          orders: [],
          totalAmount: 0,
          itemCount: 0
        };
      }
      groups[key].orders.push(order);
      groups[key].totalAmount += Number(order.totalAmount) || 0;
      groups[key].itemCount += Number(order.itemCount) || (order.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    });
    return Object.values(groups).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [orders]);

  const todayTotal = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return sales.reduce((sum, sale) => {
      const saleDate = sale.createdAt?.toDate ? sale.createdAt.toDate() : new Date(sale.createdAt);
      if (!saleDate || Number.isNaN(saleDate.getTime())) return sum;
      return saleDate.toISOString().slice(0, 10) === today ? sum + (sale.totalAmount || 0) : sum;
    }, 0);
  }, [sales]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const openProductModal = (product = null) => {
    setEditingProduct(product);
    setProductImageFile(null);
    setProductImagePreview(product?.imageUrl || '');
    setProductForm(product ? {
      name: product.name || '',
      category: product.category || 'food',
      price: product.price?.toString() || '',
      stock: product.stock?.toString() || '',
      trackStock: product.trackStock || false,
      imageUrl: product.imageUrl || '',
      active: product.active !== false
    } : {
      name: '',
      category: 'food',
      price: '',
      stock: '',
      trackStock: false,
      imageUrl: '',
      active: true
    });
    setShowProductModal(true);
  };

  const handleProductImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Selecione um arquivo de imagem.');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 5 MB.');
      event.target.value = '';
      return;
    }
    if (productImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(productImagePreview);
    }
    setProductImageFile(file);
    setProductImagePreview(URL.createObjectURL(file));
  };

  const uploadProductImage = async () => {
    if (!productImageFile) return productForm.imageUrl || '';
    const extension = productImageFile.name.split('.').pop()?.toLowerCase() || 'jpg';
    const imageRef = ref(
      storage,
      `companies/${companyId}/pos-products/${selectedEstablishmentId}/${Date.now()}.${extension}`
    );
    await uploadBytes(imageRef, productImageFile, { contentType: productImageFile.type });
    return getDownloadURL(imageRef);
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    const price = parseFloat(productForm.price);
    const stock = productForm.trackStock ? parseInt(productForm.stock || 0, 10) : null;

    if (!productForm.name.trim() || Number.isNaN(price) || price <= 0) {
      alert('Informe nome e preço válido para o item.');
      return;
    }

    setUploadingProductImage(true);
    let imageUrl;
    try {
      imageUrl = await uploadProductImage();
    } catch (error) {
      console.error('Erro ao enviar imagem do produto:', error);
      alert('Não foi possível enviar a imagem. Verifique sua conexão e tente novamente.');
      setUploadingProductImage(false);
      return;
    }

    const payload = {
      name: productForm.name.trim(),
      category: productForm.category,
      price,
      trackStock: productForm.trackStock,
      stock,
      imageUrl,
      active: productForm.active,
      companyId,
      establishmentId: selectedEstablishmentId,
      establishmentName: selectedEstablishment?.name || '',
      auditActorId: currentUser?.uid || '',
      auditActorEmail: userData?.email || '',
      auditActorName: userData?.name || userData?.email || ''
    };

    try {
      if (editingProduct) {
        await updateDocument(`companies/${companyId}/posProducts`, editingProduct.id, payload);
      } else {
        await addDoc(collection(db, `companies/${companyId}/posProducts`), {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      setShowProductModal(false);
      setEditingProduct(null);
      setProductImageFile(null);
      loadPointOfSale();
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      alert('Não foi possível salvar o produto.');
    } finally {
      setUploadingProductImage(false);
    }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Excluir o item "${product.name}" do PDV?`)) return;
    await deleteDocument(`companies/${companyId}/posProducts`, product.id);
    setCart(prev => prev.filter(item => item.id !== product.id));
    loadPointOfSale();
  };

  const addToCart = (product) => {
    if (product.trackStock && product.stock <= 0) {
      alert('Item sem estoque.');
      return;
    }

    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        if (product.trackStock && existing.quantity + 1 > product.stock) {
          alert('Quantidade acima do estoque disponível.');
          return prev;
        }
        return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, {
        id: product.id,
        productId: product.productId || product.id,
        source: product.source || 'pos',
        name: product.name,
        category: product.category,
        price: product.price,
        quantity: 1,
        trackStock: product.trackStock,
        stock: product.stock
      }];
    });
  };

  const changeQuantity = (productId, delta) => {
    setCart(prev => prev
      .map(item => {
        if (item.id !== productId) return item;
        const quantity = item.quantity + delta;
        if (item.trackStock && quantity > item.stock) {
          alert('Quantidade acima do estoque disponível.');
          return item;
        }
        return { ...item, quantity };
      })
      .filter(item => item.quantity > 0)
    );
  };

  const loadOrderGroupToCart = (group) => {
    setSelectedOrderGroup(group);
    setCart([]);
    setSaleData({
      customerName: group.customerName || group.label || '',
      customerPhone: group.customerPhone || '',
      customerCpf: group.customerCpf || '',
      table: group.table || '',
      paymentMethod: 'cash',
      peopleCount: 1,
      notes: [
        group.customerPhone ? `WhatsApp: ${group.customerPhone}` : '',
        group.table ? `Mesa/ref.: ${group.table}` : '',
        `${group.orders.length} pedido(s) agrupado(s)`
      ].filter(Boolean).join(' | ')
    });
  };

  const clearCart = () => {
    setCart([]);
    setSelectedOrderGroup(null);
    setSaleData({ ...initialSaleData });
  };

  const openCash = async () => {
    if (!selectedEstablishmentId || !currentUser?.uid) return;
    const openingAmount = parseFloat(cashAmount || 0);
    const sessionRef = doc(collection(db, `companies/${companyId}/cashSessions`));
    const activeCashRef = doc(db, `companies/${companyId}/activeCashRegisters`, currentUser.uid);
    const sessionData = {
      companyId,
      establishmentId: selectedEstablishmentId,
      establishmentName: selectedEstablishment?.name || '',
      status: 'open',
      openingAmount: Number.isFinite(openingAmount) ? openingAmount : 0,
      openedAt: serverTimestamp(),
      openedByUserId: currentUser.uid,
      openedByEmail: userData?.email || currentUser.email || '',
      openedByName: userData?.name || userData?.email || currentUser.email || '',
      auditActorId: currentUser.uid,
      auditActorEmail: userData?.email || currentUser.email || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (!isOnline) {
      const operation = createOfflineOperation(
        'cash_open',
        `companies/${companyId}/cashSessions/${sessionRef.id}`,
        `Abertura de caixa em ${selectedEstablishment?.name || 'ponto de venda'}`
      );
      const offlineSessionData = { ...sessionData, offlineOperationId: operation.id };
      const batch = writeBatch(db);
      batch.set(sessionRef, offlineSessionData);
      batch.set(activeCashRef, {
        userId: currentUser.uid,
        userEmail: userData?.email || currentUser.email || '',
        userName: userData?.name || userData?.email || currentUser.email || '',
        sessionId: sessionRef.id,
        establishmentId: selectedEstablishmentId,
        establishmentName: selectedEstablishment?.name || '',
        openedAt: serverTimestamp()
      });
      registerPendingOperation(operation, batch.commit());
      setCashAmount('');
      setCashSession({ id: sessionRef.id, ...offlineSessionData, openedAt: new Date() });
      alert('Caixa aberto neste dispositivo. Será validado e sincronizado quando a conexão voltar.');
      return;
    }

    try {
      const sessionsSnapshot = await getDocs(collection(db, `companies/${companyId}/cashSessions`));
      const existingSession = sessionsSnapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .find(session => session.status === 'open' && (
          session.openedByUserId === currentUser.uid ||
          (!session.openedByUserId && session.openedByEmail === userData?.email)
        ));
      if (existingSession) {
        if (existingSession.establishmentId !== selectedEstablishmentId) {
          setSelectedEstablishmentId(existingSession.establishmentId);
        }
        throw new Error(`Você já possui um caixa aberto em ${existingSession.establishmentName || 'outro ponto de venda'}.`);
      }

      await runTransaction(db, async transaction => {
        const activeCash = await transaction.get(activeCashRef);
        if (activeCash.exists()) {
          const error = new Error('Você já possui um caixa aberto.');
          error.activeEstablishmentId = activeCash.data().establishmentId;
          throw error;
        }
        transaction.set(sessionRef, sessionData);
        transaction.set(activeCashRef, {
          userId: currentUser.uid,
          userEmail: userData?.email || currentUser.email || '',
          userName: userData?.name || userData?.email || currentUser.email || '',
          sessionId: sessionRef.id,
          establishmentId: selectedEstablishmentId,
          establishmentName: selectedEstablishment?.name || '',
          openedAt: serverTimestamp()
        });
      });
      setCashAmount('');
      setCashSession({ id: sessionRef.id, ...sessionData });
    } catch (error) {
      console.error('Erro ao abrir caixa:', error);
      if (error.activeEstablishmentId && error.activeEstablishmentId !== selectedEstablishmentId) {
        setSelectedEstablishmentId(error.activeEstablishmentId);
      }
      alert(error.message || 'Não foi possível abrir o caixa.');
    }
  };

  const closeCash = async () => {
    if (!cashSession) return;
    const closingAmount = parseFloat(cashAmount || 0);
    const sessionSales = sales.filter(sale => sale.cashSessionId === cashSession.id);
    const salesTotal = sessionSales.reduce((sum, sale) => sum + (Number(sale.totalAmount) || 0), 0);
    const sessionRef = doc(db, `companies/${companyId}/cashSessions`, cashSession.id);
    const activeCashRef = doc(db, `companies/${companyId}/activeCashRegisters`, currentUser.uid);
    const closeData = {
      status: 'closed',
      closingAmount: Number.isFinite(closingAmount) ? closingAmount : 0,
      salesTotal,
      expectedAmount: (Number(cashSession.openingAmount) || 0) + salesTotal,
      closedAt: serverTimestamp(),
      closedByUserId: currentUser.uid,
      closedByEmail: userData?.email || currentUser.email || '',
      auditActorId: currentUser.uid,
      auditActorEmail: userData?.email || currentUser.email || '',
      updatedAt: serverTimestamp()
    };

    if (!isOnline) {
      const operation = createOfflineOperation(
        'cash_close',
        `companies/${companyId}/cashSessions/${cashSession.id}`,
        `Fechamento de caixa em ${cashSession.establishmentName || 'ponto de venda'}`
      );
      const batch = writeBatch(db);
      batch.update(sessionRef, { ...closeData, offlineOperationId: operation.id });
      batch.delete(activeCashRef);
      registerPendingOperation(operation, batch.commit());
      setCashAmount('');
      setCashSession(null);
      clearCart();
      alert('Caixa fechado neste dispositivo. Será sincronizado quando a conexão voltar.');
      return;
    }

    try {
      await runTransaction(db, async transaction => {
        const activeCash = await transaction.get(activeCashRef);
        if (activeCash.exists() && activeCash.data().sessionId !== cashSession.id) {
          throw new Error('Este caixa não está vinculado ao seu usuário.');
        }
        transaction.update(sessionRef, closeData);
        if (activeCash.exists()) transaction.delete(activeCashRef);
      });
      setCashAmount('');
      setCashSession(null);
      clearCart();
      loadPointOfSale();
    } catch (error) {
      console.error('Erro ao fechar caixa:', error);
      alert(error.message || 'Não foi possível fechar o caixa.');
    }
  };

  const queueOrder = (orderPayload, successMessage) => {
    const orderRef = doc(collection(db, `companies/${companyId}/orders`));
    const operation = createOfflineOperation(
      'order',
      `companies/${companyId}/orders/${orderRef.id}`,
      `Pedido de ${orderPayload.customerName || orderPayload.table || 'cliente'}`
    );
    const persistedPayload = {
      ...orderPayload,
      offlineOperationId: operation.id
    };
    const batch = writeBatch(db);
    batch.set(orderRef, persistedPayload);

    const trackingRef = getTrackingRef(orderRef.id, persistedPayload.customerPhone);
    if (trackingRef) {
      batch.set(trackingRef, buildOrderTrackingPayload({
        id: orderRef.id,
        ...persistedPayload
      }, {
        orderId: orderRef.id,
        paymentStatus: 'pending',
        status: 'new',
        createdAt: serverTimestamp()
      }), { merge: true });
    }

    registerPendingOperation(operation, batch.commit());
    setOrders(current => [{
      id: orderRef.id,
      ...persistedPayload,
      createdAt: new Date(),
      updatedAt: new Date()
    }, ...current]);
    clearCart();
    alert(isOnline ? successMessage : `${successMessage} Será sincronizado quando a conexão voltar.`);
  };

  const addOrderToSelectedGroup = async () => {
    if (cart.length === 0) {
      alert('Adicione itens ao carrinho para incluir no pedido da mesa.');
      return;
    }
    if (!selectedOrderGroup) {
      alert('Selecione uma mesa antes de adicionar o pedido.');
      return;
    }

    const customerData = buildCustomerComandaData({
      customerName: saleData.customerName,
      customerPhone: saleData.customerPhone,
      customerCpf: saleData.customerCpf,
      table: saleData.table,
      notes: saleData.notes
    });

    try {
      const orderPayload = {
        companyId,
        establishmentId: selectedEstablishmentId,
        establishmentName: selectedEstablishment?.name || '',
        customerName: customerData.customerName || selectedOrderGroup.customerName || '',
        customerPhone: customerData.customerPhone || selectedOrderGroup.customerPhone || '',
        customerCpf: customerData.customerCpf || selectedOrderGroup.customerCpf || '',
        table: customerData.table || selectedOrderGroup.table || '',
        notes: customerData.notes || selectedOrderGroup.notes || '',
        source: 'pos_table_addition',
        status: 'new',
        paymentStatus: 'pending',
        items: cart.map(item => ({
          menuItemId: item.productId || item.id,
          productId: item.productId || item.id,
          source: item.source || 'pos',
          name: item.name,
          category: item.category || '',
          price: Number(item.price) || 0,
          quantity: Number(item.quantity) || 1,
          total: (Number(item.price) || 0) * (Number(item.quantity) || 1)
        })),
        itemCount: totals.itemCount,
        totalAmount: totals.subtotal,
        createdByUserId: currentUser?.uid || '',
        createdByEmail: userData?.email || '',
        createdByName: userData?.name || userData?.email || '',
        createdByPhone: userData?.phone || currentUser?.phoneNumber || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      queueOrder(orderPayload, 'Pedido adicionado à mesa selecionada.');
    } catch (error) {
      console.error('Erro ao adicionar pedido à mesa:', error);
      alert('Erro ao adicionar pedido. Tente novamente.');
    }
  };

  const updatePaymentRow = (index, field, value) => {
    setPaymentBreakdown(current => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [field]: value } : row
    )));
    if (field === 'method') {
      setSaleData(current => ({ ...current, paymentMethod: value }));
    }
  };

  const addPaymentRow = () => {
    setPaymentBreakdown(current => [...current, { method: 'cash', amount: '' }]);
  };

  const removePaymentRow = (index) => {
    setPaymentBreakdown(current => {
      if (current.length === 1) {
        return [{ method: 'cash', amount: '' }];
      }
      return current.filter((_, rowIndex) => rowIndex !== index);
    });
  };

  const openTableOrder = async () => {
    if (cart.length === 0) {
      alert('Adicione itens ao carrinho para abrir a mesa.');
      return;
    }

    const customerData = buildCustomerComandaData({
      customerName: saleData.customerName,
      customerPhone: saleData.customerPhone,
      customerCpf: saleData.customerCpf,
      table: saleData.table,
      notes: saleData.notes
    });

    if (!customerData.customerName) {
      alert('Informe o nome do cliente para abrir a mesa.');
      return;
    }

    try {
      const orderPayload = {
        companyId,
        establishmentId: selectedEstablishmentId,
        establishmentName: selectedEstablishment?.name || '',
        customerName: customerData.customerName,
        customerPhone: customerData.customerPhone,
        customerCpf: customerData.customerCpf,
        table: customerData.table,
        notes: customerData.notes,
        source: 'pos_table',
        status: 'new',
        paymentStatus: 'pending',
        items: cart.map(item => ({
          menuItemId: item.productId || item.id,
          productId: item.productId || item.id,
          source: item.source || 'pos',
          name: item.name,
          category: item.category || '',
          price: Number(item.price) || 0,
          quantity: Number(item.quantity) || 1,
          total: (Number(item.price) || 0) * (Number(item.quantity) || 1)
        })),
        itemCount: totals.itemCount,
        totalAmount: totals.subtotal,
        createdByUserId: currentUser?.uid || '',
        createdByEmail: userData?.email || '',
        createdByName: userData?.name || userData?.email || '',
        createdByPhone: userData?.phone || currentUser?.phoneNumber || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      queueOrder(orderPayload, 'Mesa aberta. A comanda ficou registrada em pedidos.');
    } catch (error) {
      console.error('Erro ao abrir mesa:', error);
      alert('Erro ao abrir a mesa. Tente novamente.');
    }
  };

  const finishSale = async () => {
    if (!cashSession) {
      alert('Abra o caixa antes de finalizar vendas.');
      return;
    }
    if (!selectedOrderGroup && cart.length === 0) {
      alert('Adicione itens ao carrinho ou selecione uma mesa para fechar a venda.');
      return;
    }

    const confirmed = window.confirm(
      selectedOrderGroup
        ? `Fechar a mesa/comanda de ${selectedOrderGroup.customerName || selectedOrderGroup.label || 'cliente'}?\n\nEssa ação registra o pagamento e conclui todos os pedidos da mesa.`
        : 'Tem certeza que deseja fechar esta venda?'
    );

    if (!confirmed) return;

    const customerData = buildCustomerComandaData({
      customerName: saleData.customerName,
      customerPhone: saleData.customerPhone,
      customerCpf: saleData.customerCpf,
      table: saleData.table,
      notes: saleData.notes
    });

    if (!customerData.customerName && !selectedOrderGroup) {
      alert('Informe o nome do cliente para registrar a comanda.');
      return;
    }

    const saleItems = selectedOrderGroup && cart.length === 0
      ? selectedGroupSummary
      : cart;
    const saleTotal = selectedOrderGroup && cart.length === 0
      ? selectedGroupTotal
      : totals.subtotal;
    const saleItemCount = selectedOrderGroup && cart.length === 0
      ? selectedGroupSummary.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
      : totals.itemCount;

    const normalizedBreakdown = paymentBreakdown
      .map(row => ({
        method: row.method || 'cash',
        amount: Number(row.amount) || 0
      }))
      .filter(row => row.amount > 0);

    if (normalizedBreakdown.length === 0) {
      alert('Informe ao menos um valor para registrar o pagamento.');
      return;
    }

    const paymentCheck = calculatePaymentBreakdown({
      totalAmount: saleTotal,
      peopleCount: Number(saleData.peopleCount) || 1,
      paymentBreakdown: normalizedBreakdown
    });

    if (!paymentCheck.isExact) {
      alert(`A soma dos pagamentos deve fechar exatamente o valor da conta. Faltam R$ ${Math.abs(paymentCheck.remainingAmount).toFixed(2).replace('.', ',')} para fechar.`);
      return;
    }

    setSavingSale(true);
    try {
      const paymentRecord = buildPosPaymentRecord({
        paymentBreakdown: normalizedBreakdown,
        userId: currentUser?.uid || '',
        userEmail: userData?.email || currentUser?.email || '',
        userName: userData?.name || userData?.email || currentUser?.email || '',
        isOnline: navigator.onLine,
        recordedAt: new Date()
      });
      const sale = {
        companyId,
        establishmentId: selectedEstablishmentId,
        establishmentName: selectedEstablishment?.name || '',
        orderId: selectedOrderGroup?.orders?.[0]?.id || '',
        orderIds: selectedOrderGroup?.orders?.map(order => order.id) || [],
        cashSessionId: cashSession.id,
        source: selectedOrderGroup ? 'order_group' : 'counter',
        customerName: customerData.customerName || selectedOrderGroup?.customerName || selectedOrderGroup?.label || 'Cliente',
        customerPhone: customerData.customerPhone || selectedOrderGroup?.customerPhone || '',
        customerCpf: customerData.customerCpf || selectedOrderGroup?.customerCpf || '',
        table: customerData.table || selectedOrderGroup?.table || '',
        ...paymentRecord,
        peopleCount: Number(saleData.peopleCount) || 1,
        perPersonValue: paymentCheck.perPersonValue,
        notes: customerData.notes || selectedOrderGroup?.orders?.[0]?.notes || '',
        items: saleItems.map(normalizeSaleItem),
        itemCount: saleItemCount,
        totalAmount: saleTotal,
        status: 'paid',
        paymentStatus: 'paid',
        paymentReceivedAt: serverTimestamp(),
        createdByUserId: currentUser?.uid || '',
        createdByEmail: userData?.email || ''
      };

      const saleRef = doc(collection(db, `companies/${companyId}/posSales`));
      const operation = createOfflineOperation(
        'sale',
        `companies/${companyId}/posSales/${saleRef.id}`,
        `Fechamento de ${customerData.customerName}`
      );
      operation.totalAmount = saleTotal;
      operation.paymentMethod = paymentRecord.paymentMethod;
      operation.orderIds = sale.orderIds;
      const persistedSale = {
        ...sale,
        offlineOperationId: operation.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const batch = writeBatch(db);
      batch.set(saleRef, persistedSale);

      cart
        .filter(item => item.trackStock && item.source === 'pos')
        .forEach(item => {
          batch.update(doc(db, `companies/${companyId}/posProducts`, item.id), {
            stock: Math.max((item.stock || 0) - item.quantity, 0),
            auditActorId: currentUser?.uid || '',
            auditActorEmail: userData?.email || '',
            updatedAt: serverTimestamp()
          });
        });

      if (selectedOrderGroup) {
        selectedOrderGroup.orders.forEach(order => {
          const orderUpdates = {
            status: 'delivered',
            paymentStatus: 'paid',
            paidAmount: Number(order.totalAmount) || 0,
            paymentMethod: paymentRecord.paymentMethod,
            paymentBreakdown: paymentRecord.paymentBreakdown,
            paymentRecordedByUserId: paymentRecord.paymentRecordedByUserId,
            paymentRecordedByEmail: paymentRecord.paymentRecordedByEmail,
            paymentRecordedByName: paymentRecord.paymentRecordedByName,
            paymentDeviceRecordedAt: paymentRecord.deviceRecordedAt,
            paymentRecordedOffline: paymentRecord.recordedOffline,
            customerName: customerData.customerName || order.customerName || '',
            customerPhone: customerData.customerPhone || order.customerPhone || '',
            customerCpf: customerData.customerCpf || order.customerCpf || '',
            table: customerData.table || order.table || '',
            notes: customerData.notes || order.notes || '',
            posSaleId: saleRef.id,
            paidAt: serverTimestamp(),
            deliveredAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          batch.update(doc(db, `companies/${companyId}/orders`, order.id), orderUpdates);
          const trackingRef = getTrackingRef(order.id, orderUpdates.customerPhone);
          if (trackingRef) {
            batch.set(trackingRef, buildOrderTrackingPayload(order, {
              ...orderUpdates,
              paymentStatus: 'paid'
            }), { merge: true });
          }
          const notification = buildOrderStatusNotification(order, 'delivered');
          if (notification) {
            batch.set(doc(db, `companies/${companyId}/userNotifications`, `${order.id}_delivered`), {
              ...notification,
              companyId,
              read: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            }, { merge: true });
          }
        });
      }

      registerPendingOperation(operation, batch.commit());
      setSales(current => [{
        id: saleRef.id,
        ...persistedSale,
        createdAt: new Date(),
        updatedAt: new Date()
      }, ...current]);
      setProducts(current => current.map(product => {
        const soldItem = cart.find(item => item.id === product.id && item.trackStock && item.source === 'pos');
        return soldItem
          ? { ...product, stock: Math.max((product.stock || 0) - soldItem.quantity, 0) }
          : product;
      }));
      if (selectedOrderGroup) {
        const paidOrderIds = new Set(selectedOrderGroup.orders.map(order => order.id));
        setOrders(current => current.filter(order => !paidOrderIds.has(order.id)));
      }

      clearCart();
      alert(isOnline
        ? 'Conta fechada com sucesso.'
        : 'Conta fechada neste dispositivo. A sincronização será feita quando a conexão voltar.');
    } catch (error) {
      console.error('Erro ao finalizar venda:', error);
      alert('Erro ao finalizar venda.');
    } finally {
      setSavingSale(false);
    }
  };

  if (loading) {
    return <LoadingState title="Carregando PDV" subtitle="Atualizando produtos, vendas e caixa" />;
  }

  return (
    <div className="pos-container">
      <div className="pos-header">
        <div>
          <h1>PDV</h1>
          <p>{selectedEstablishment ? `Caixa de ${selectedEstablishment.name}` : 'Selecione um ponto de venda para abrir o caixa.'}</p>
        </div>
        <button className="pos-primary-button" onClick={() => openProductModal()} disabled={!selectedEstablishmentId}>
          <Plus size={18} />
          Novo Item
        </button>
      </div>

      {(!isOnline || pendingOperations.length > 0) && (
        <section className={`pos-sync-status ${!isOnline ? 'is-offline' : 'is-syncing'}`} role="status">
          {!isOnline ? <CloudOff size={22} /> : <RefreshCw size={22} className="is-spinning" />}
          <div>
            <strong>
              {!isOnline
                ? 'PDV offline'
                : pendingOperations.some(operation => operation.status === 'error')
                  ? 'Há operações que precisam de atenção'
                  : 'Sincronizando o PDV'}
            </strong>
            <span>
              {pendingOperations.length > 0
                ? `${pendingOperations.length} operação(ões) salva(s) neste dispositivo aguardando confirmação.`
                : 'Pedidos e fechamentos serão salvos neste dispositivo até a conexão voltar.'}
            </span>
          </div>
        </section>
      )}

      <section className="pos-establishments">
        <h2>Ponto de venda do caixa</h2>
        <div className="pos-establishment-grid">
          {establishments.map(establishment => (
            <button
              key={establishment.id}
              className={selectedEstablishmentId === establishment.id ? 'active' : ''}
              onClick={() => {
                setSelectedEstablishmentId(establishment.id);
                clearCart();
              }}
            >
              <Building2 size={20} />
              <span>{establishment.name}</span>
              <small>{establishment.type === 'bar' ? 'Bar' : establishment.type === 'kiosk' ? 'Quiosque' : 'Restaurante'}</small>
            </button>
          ))}
        </div>
        {establishments.length === 0 && <p className="pos-cart-empty">Nenhum bar, restaurante ou quiosque disponível para este usuário.</p>}
      </section>

      {!selectedEstablishmentId ? (
        <div className="pos-empty">
          <Building2 size={42} />
          <p>Selecione um ponto de venda para ver pedidos, produtos e vendas do caixa.</p>
        </div>
      ) : loadingRegister ? (
        <LoadingState variant="block" title="Carregando caixa" subtitle="Buscando pedidos e itens do ponto de venda" />
      ) : (
      <>

      <section className={`pos-cash ${cashSession ? 'is-open' : 'is-closed'}`}>
        <div>
          <h2>{cashSession ? 'Caixa aberto' : 'Caixa fechado'}</h2>
          <p>
            {cashSession
              ? `Abertura: ${formatCurrency(cashSession.openingAmount || 0)}`
              : 'Abra o caixa para cobrar pedidos e vendas balcão.'}
          </p>
        </div>
        <div className="pos-cash-actions">
          <input
            type="number"
            step="0.01"
            value={cashAmount}
            onChange={(event) => setCashAmount(event.target.value)}
            placeholder={cashSession ? 'Valor no caixa ao fechar' : 'Valor inicial'}
          />
          {cashSession ? (
            <button onClick={closeCash}>Fechar caixa</button>
          ) : (
            <button onClick={openCash}>Abrir caixa</button>
          )}
        </div>
      </section>

      <div className="pos-summary">
        <div className="pos-summary-item">
          <Package size={22} />
          <div>
            <span>Itens ativos</span>
            <strong>{products.filter(product => product.active !== false).length}</strong>
          </div>
        </div>
        <div className="pos-summary-item">
          <Receipt size={22} />
          <div>
            <span>Vendas recentes</span>
            <strong>{sales.length}</strong>
          </div>
        </div>
        <div className="pos-summary-item">
          <ShoppingCart size={22} />
          <div>
            <span>Faturamento hoje</span>
            <strong>{formatCurrency(todayTotal)}</strong>
          </div>
        </div>
      </div>

      <section className="pos-orders">
        <div className="pos-section-header">
          <h2>Comandas aguardando cobrança</h2>
          <span>{orderGroups.length}</span>
        </div>
        <div className="pos-order-list">
          {orderGroups.map(group => (
            <button
              key={group.key}
              className={`pos-order-card ${selectedOrderGroup?.key === group.key ? 'active' : ''}`}
              onClick={() => loadOrderGroupToCart(group)}
            >
              <strong>{group.label}</strong>
              <span>{group.type} · {group.orders.length} pedido(s) · {group.itemCount} itens</span>
              <b>{formatCurrency(group.totalAmount)}</b>
            </button>
          ))}
          {orderGroups.length === 0 && <p className="pos-cart-empty">Nenhum pedido aguardando cobrança.</p>}
        </div>
      </section>

      <div className={`pos-layout ${showProductsPanel ? 'show-products' : 'hide-products'}`}>
        {showProductsPanel && (
          <section className="pos-products">
            <div className="pos-search-wrap">
              <div className="pos-search">
                <Search size={18} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar item ou categoria"
                />
              </div>
              <button
                type="button"
                className="pos-collapse-button"
                onClick={() => setShowProductsPanel(false)}
                title="Minimizar painel de produtos"
              >
                <ChevronLeft size={18} />
                Minimize
              </button>
            </div>

            <div className="pos-product-grid">
              {filteredProducts.map(product => (
                <article key={product.id} className="pos-product">
                  <button className="pos-product-main" onClick={() => addToCart(product)}>
                    <div className="pos-product-image">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} loading="lazy" />
                      ) : (
                        <Package size={34} aria-hidden="true" />
                      )}
                    </div>
                    <strong>{product.name}</strong>
                    <span>{getCategoryLabel(product.category)}</span>
                    <b>{formatCurrency(product.price)}</b>
                    {product.trackStock && <small>Estoque: {product.stock || 0}</small>}
                  </button>
                  {product.source === 'pos' && (
                    <div className="pos-product-actions">
                      <button onClick={() => openProductModal(product)}>Editar</button>
                      <button className="danger" onClick={() => deleteProduct(product)}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="pos-empty">
                <Package size={42} />
                <p>Nenhum item encontrado no PDV.</p>
              </div>
            )}
          </section>
        )}

        {!showProductsPanel && (
          <button
            type="button"
            className="pos-expand-button"
            onClick={() => setShowProductsPanel(true)}
            title="Mostrar painel de produtos"
          >
            <ChevronRight size={18} />
            Produtos
          </button>
        )}

        <aside className="pos-cart">
          <div className="pos-cart-header">
            <h2>Carrinho</h2>
            <span>{selectedOrderGroup ? selectedOrderGroup.type : `${totals.itemCount} itens`}</span>
          </div>

          <div className="pos-cart-items">
            {cart.map(item => (
              <div key={item.id} className="pos-cart-item">
                <div>
                  <strong>{item.name}</strong>
                  <span>{formatCurrency(item.price)}</span>
                </div>
                <div className="pos-quantity">
                  <button onClick={() => changeQuantity(item.id, -1)}><Minus size={14} /></button>
                  <b>{item.quantity}</b>
                  <button onClick={() => changeQuantity(item.id, 1)}><Plus size={14} /></button>
                </div>
              </div>
            ))}
            {cart.length === 0 && <p className="pos-cart-empty">Selecione itens para iniciar uma venda.</p>}
          </div>

          {selectedOrderGroup && (
            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>Pedidos da mesa</strong>
                <span>{selectedOrderGroup.orders.length} pedido(s)</span>
              </div>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {selectedOrderGroup.orders.map(order => (
                  <div key={order.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.6rem 0.75rem', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.35rem' }}>
                      <strong style={{ fontSize: '0.82rem' }}>{order.customerName || 'Pedido'}</strong>
                      <span style={{ fontSize: '0.8rem', color: '#475569' }}>{formatCurrency(order.totalAmount)}</span>
                    </div>
                    <div style={{ display: 'grid', gap: '0.2rem' }}>
                      {(order.items || []).map(item => (
                        <span key={`${order.id}-${item.productId || item.menuItemId || item.name}`} style={{ fontSize: '0.75rem', color: '#475569' }}>
                          {item.name} x {item.quantity}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pos-sale-form">
            <label>
              Nome do cliente
              <input
                value={saleData.customerName || ''}
                onChange={(event) => setSaleData({ ...saleData, customerName: event.target.value })}
                placeholder="Obrigatório"
              />
            </label>
            <div className="pos-sale-dual-row">
              <label>
                WhatsApp
                <input
                  value={saleData.customerPhone || ''}
                  onChange={(event) => setSaleData({ ...saleData, customerPhone: event.target.value })}
                  placeholder="Opcional"
                />
              </label>
              <label>
                CPF
                <input
                  value={saleData.customerCpf || ''}
                  onChange={(event) => setSaleData({ ...saleData, customerCpf: event.target.value })}
                  placeholder="Opcional"
                />
              </label>
            </div>
            <label>
              Mesa / referência
              <input
                value={saleData.table || ''}
                onChange={(event) => setSaleData({ ...saleData, table: event.target.value })}
                placeholder="Opcional"
              />
            </label>
            <div className="pos-sale-dual-row">
              <label>
                Pessoas
                <input
                  type="number"
                  min="1"
                  value={saleData.peopleCount || 1}
                  onChange={(event) => setSaleData({ ...saleData, peopleCount: Math.max(1, Number(event.target.value) || 1) })}
                />
              </label>
              <label>
                Valor por pessoa
                <input
                  value={formatCurrency(paymentSummary.perPersonValue)}
                  readOnly
                />
              </label>
            </div>

            <div style={{ display: 'grid', gap: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <strong>Pagamento</strong>
                <button type="button" className="pos-clear-button" onClick={addPaymentRow} style={{ width: 'auto', marginTop: 0, padding: '0.45rem 0.8rem' }}>
                  + parcelar
                </button>
              </div>
              {paymentBreakdown.map((row, index) => (
                <div key={`payment-row-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', alignItems: 'end' }}>
                  <label>
                    Forma
                    <select
                      value={row.method || 'cash'}
                      onChange={(event) => updatePaymentRow(index, 'method', event.target.value)}
                    >
                      {paymentMethods.map(method => (
                        <option key={method.value} value={method.value}>{method.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Valor
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={row.amount ?? ''}
                      onChange={(event) => updatePaymentRow(index, 'amount', event.target.value)}
                    />
                  </label>
                  <button type="button" className="pos-clear-button" onClick={() => removePaymentRow(index)} style={{ width: 'auto', marginTop: '1.35rem', padding: '0.7rem 0.8rem' }}>
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gap: '0.2rem', padding: '0.75rem 0.8rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Total pago</span>
              <strong>{formatCurrency(paymentSummary.splitTotal)}</strong>
              <span style={{ fontSize: '0.8rem', color: paymentSummary.remainingAmount === 0 ? '#15803d' : '#b45309' }}>
                {paymentSummary.remainingAmount === 0
                  ? 'Pagamento exato'
                  : `Falta ${formatCurrency(Math.abs(paymentSummary.remainingAmount))}`}
              </span>
            </div>

            <label>
              Observações
              <textarea
                value={saleData.notes || ''}
                onChange={(event) => setSaleData({ ...saleData, notes: event.target.value })}
                rows="2"
                placeholder="Opcional"
              />
            </label>
          </div>

          <div className="pos-total">
            <span>Total</span>
            <strong>{formatCurrency(selectedOrderGroup ? selectedGroupTotal : totals.subtotal)}</strong>
          </div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {selectedOrderGroup ? (
              <button className="pos-clear-button" onClick={addOrderToSelectedGroup} disabled={savingSale || cart.length === 0}>
                Adicionar pedido
              </button>
            ) : (
              <button className="pos-clear-button" onClick={openTableOrder} disabled={savingSale || cart.length === 0}>
                Abrir mesa
              </button>
            )}
            <button className="pos-finish-button" onClick={finishSale} disabled={savingSale || (!selectedOrderGroup && cart.length === 0) || !cashSession}>
              {savingSale ? 'Finalizando...' : cashSession ? 'Finalizar Venda' : 'Abra o caixa'}
            </button>
          </div>
          {cart.length > 0 && (
            <button className="pos-clear-button" onClick={clearCart}>
              Limpar carrinho
            </button>
          )}
        </aside>
      </div>

      <section className="pos-history">
        <h2>Últimas vendas</h2>
        <div className="pos-history-list">
          {sales.slice(0, 8).map(sale => (
            <div key={sale.id} className="pos-history-item">
              <div>
                <strong>{sale.customerName || 'Venda balcão'}</strong>
                <span>{sale.itemCount || 0} itens · {paymentMethods.find(method => method.value === sale.paymentMethod)?.label || sale.paymentMethod}</span>
              </div>
              <b>{formatCurrency(sale.totalAmount)}</b>
            </div>
          ))}
          {sales.length === 0 && <p className="pos-cart-empty">Nenhuma venda registrada.</p>}
        </div>
      </section>
      </>
      )}

      {showProductModal && (
        <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="modal-content pos-product-modal" onClick={(event) => event.stopPropagation()}>
            <div className="pos-modal-header">
              <h2>{editingProduct ? 'Editar Item' : 'Novo Item'}</h2>
              <button onClick={() => setShowProductModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={saveProduct}>
              <div className="form-group">
                <label>Imagem do produto</label>
                <div className="pos-image-upload">
                  {productImagePreview ? (
                    <div className="pos-image-preview">
                      <img src={productImagePreview} alt="Pré-visualização do produto" />
                      <button
                        type="button"
                        onClick={() => {
                          if (productImagePreview.startsWith('blob:')) {
                            URL.revokeObjectURL(productImagePreview);
                          }
                          setProductImageFile(null);
                          setProductImagePreview('');
                          setProductForm(current => ({ ...current, imageUrl: '' }));
                        }}
                        aria-label="Remover imagem"
                        title="Remover imagem"
                      >
                        <X size={17} />
                      </button>
                    </div>
                  ) : (
                    <label className="pos-image-upload-label">
                      <input type="file" accept="image/*" onChange={handleProductImageSelect} />
                      <ImagePlus size={30} />
                      <span>Selecionar imagem</span>
                      <small>JPG, PNG ou WebP, até 5 MB</small>
                    </label>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Nome</label>
                <input
                  value={productForm.name}
                  onChange={(event) => setProductForm({ ...productForm, name: event.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Categoria</label>
                  <select
                    value={productForm.category}
                    onChange={(event) => setProductForm({ ...productForm, category: event.target.value })}
                  >
                    {productCategories.map(category => (
                      <option key={category.value} value={category.value}>{category.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Preço</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={productForm.price}
                    onChange={(event) => setProductForm({ ...productForm, price: event.target.value })}
                    required
                  />
                </div>
              </div>
              <label className="pos-checkbox">
                <input
                  type="checkbox"
                  checked={productForm.trackStock}
                  onChange={(event) => setProductForm({ ...productForm, trackStock: event.target.checked })}
                />
                Controlar estoque
              </label>
              {productForm.trackStock && (
                <div className="form-group">
                  <label>Estoque</label>
                  <input
                    type="number"
                    min="0"
                    value={productForm.stock}
                    onChange={(event) => setProductForm({ ...productForm, stock: event.target.value })}
                  />
                </div>
              )}
              <label className="pos-checkbox">
                <input
                  type="checkbox"
                  checked={productForm.active}
                  onChange={(event) => setProductForm({ ...productForm, active: event.target.checked })}
                />
                Ativo no PDV
              </label>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowProductModal(false)} disabled={uploadingProductImage}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={uploadingProductImage}>
                  {uploadingProductImage ? 'Enviando imagem...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointOfSale;
